import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { AlertTriangle, ChevronDown, Play, Loader2, CheckCircle2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { track, EVENTS } from "@/lib/analytics";

const STAGES = {
  idle: { label: "Standby", color: "#94A3B8" },
  alert: { label: "Outage Detected", color: "#EF4444" },
  routing: { label: "Orchestrating Failover...", color: "#F59E0B" },
  success: { label: "Failover Successful", color: "#10B981" },
};

export default function FailoverSimulator({ onComplete }) {
  const { tenant } = useTenant();
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [stage, setStage] = useState("idle");
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tenant) return;
    api.get(`/dashboard/units?tenant=${tenant.id}`).then(({ data }) => {
      const branches = data.filter((u) => u.type !== "hub");
      setUnits(branches);
      if (!unitId && branches.length) setUnitId(branches[0].id);
    });
  }, [tenant]);

  const selected = units.find((u) => u.id === unitId);

  const simulate = async () => {
    if (!unitId || stage === "routing") return;
    setStage("alert");
    setDetail(null);
    track(EVENTS.SIMULATE_LINK_DROP, { unit_id: unitId, source: "failover_widget" });
    try {
      const { data } = await api.post("/simulate/link-drop", { unit_id: unitId });
      setDetail(data);
      // Stage 1: outage visible briefly
      await new Promise((r) => setTimeout(r, 1000));
      // Stage 2: orchestrating
      setStage("routing");
      // Trigger actual failover playbook (real backend execution log)
      const runPromise = api.post(`/playbooks/${data.recommended_playbook}/run`);
      await new Promise((r) => setTimeout(r, 3000));
      await runPromise;
      // Stage 3: success
      setStage("success");
      toast.success("Failover orchestrated", {
        description: `Traffic now routed via ${data.backup_carrier} for ${data.site}`,
        action: { label: "Console", onClick: () => navigate("/agents?tab=playbooks") },
      });
      onComplete?.();
    } catch (e) {
      setStage("idle");
      toast.error("Simulation failed");
    }
  };

  const reset = () => {
    setStage("idle");
    setDetail(null);
    onComplete?.();
  };

  const s = STAGES[stage];

  return (
    <div className="p-5 space-y-4" data-testid="failover-simulator">
      {/* Status pill */}
      <div className="flex items-center gap-2">
        <span
          className="text-[10px] font-mono font-bold px-2.5 py-1 border rounded-sm uppercase tracking-wider"
          style={{ color: s.color, borderColor: s.color + "55", background: s.color + "12" }}
          data-testid="failover-stage"
        >
          {stage !== "idle" && <span className="evos-blink mr-1">●</span>}
          {s.label}
        </span>
      </div>

      {/* Unit selector */}
      <div>
        <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 block mb-2">Remote Unit</label>
        <div className="relative">
          <button
            data-testid="failover-unit-dropdown"
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={stage !== "idle" && stage !== "success"}
            className="w-full bg-[#020617] border border-slate-800 hover:border-slate-700 px-3 py-2.5 text-sm text-left flex items-center justify-between rounded-sm transition-colors disabled:opacity-50"
          >
            <span>{selected ? selected.name : "Select unit..."}</span>
            <ChevronDown className="w-4 h-4 text-slate-500" />
          </button>
          {open && (
            <div className="absolute z-20 top-full mt-1 left-0 right-0 max-h-60 overflow-y-auto border border-slate-800 bg-[#0F172A] rounded-sm shadow-2xl">
              {units.map((u) => (
                <button
                  key={u.id}
                  data-testid={`failover-unit-${u.id}`}
                  onClick={() => { setUnitId(u.id); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-[#1E293B] border-b border-slate-800/50 ${
                    unitId === u.id ? "bg-[#1E3A8A]/20 text-white" : "text-slate-300"
                  }`}
                >
                  <div className="font-semibold">{u.name}</div>
                  <div className="text-[10px] font-mono text-slate-500">{u.id} · {u.vendor}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Simulate button */}
      {(stage === "idle") && (
        <button
          data-testid="simulate-outage-btn"
          onClick={simulate}
          disabled={!unitId}
          className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-900 font-bold px-4 py-3 rounded-sm text-sm flex items-center justify-center gap-2 transition-colors uppercase tracking-wider"
        >
          <Play className="w-4 h-4" />
          Simulate Primary Link Outage
        </button>
      )}

      {/* Live visual flow */}
      {(stage !== "idle") && (
        <div className="space-y-3" data-testid="failover-flow">
          {/* Outage alert */}
          <div className="border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-xs">
              <div className="font-semibold text-red-200 mb-1">Outage Detected · {detail?.site || selected?.name}</div>
              <div className="text-red-300/80 text-[11px]">
                Primary <span className="font-mono">Brisanet</span> link is unreachable. Severity: Critical.
              </div>
            </div>
          </div>

          {/* Primary link */}
          <LinkRow
            carrier="Brisanet"
            role="Primary"
            status="DOWN"
            color="#EF4444"
            highlight
          />

          {/* Routing arrow */}
          {stage === "routing" && (
            <div className="flex items-center gap-2 px-3 py-2 border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs" data-testid="orchestrating-state">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="font-mono uppercase tracking-wider">Orchestrating Failover...</span>
            </div>
          )}
          {stage === "success" && (
            <div className="flex items-center gap-2 px-3 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-xs" data-testid="failover-success">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-mono uppercase tracking-wider">Failover Successful! Traffic routed to backup link (Claro)</span>
            </div>
          )}

          {/* Backup link */}
          <LinkRow
            carrier="Claro"
            role="Backup 1"
            status={stage === "success" ? "ACTIVE - ROUTING TRAFFIC" : "Standby"}
            color={stage === "success" ? "#10B981" : "#94A3B8"}
            highlight={stage === "success"}
          />

          {/* Reset / details */}
          {stage === "success" && (
            <div className="flex gap-2 pt-2">
              <button
                data-testid="failover-reset"
                onClick={reset}
                className="flex-1 border border-slate-800 hover:border-slate-700 text-slate-300 text-xs font-mono uppercase tracking-wider px-3 py-2 rounded-sm transition-colors"
              >
                Reset Simulation
              </button>
              <button
                onClick={() => navigate("/agents?tab=playbooks")}
                className="flex items-center gap-1 bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white text-xs font-semibold px-3 py-2 rounded-sm transition-colors"
              >
                View Playbook <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LinkRow({ carrier, role, status, color, highlight }) {
  return (
    <div
      className={`flex items-center gap-3 p-3 border rounded-sm transition-colors ${
        highlight ? "border-current" : "border-slate-800 bg-[#020617]"
      }`}
      style={highlight ? { borderColor: color + "80", background: color + "10" } : {}}
    >
      <div className="w-8 h-8 flex items-center justify-center border" style={{ borderColor: color + "55", background: color + "12" }}>
        <span className="font-mono font-bold text-[10px]" style={{ color }}>{carrier.slice(0,2).toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-100">{carrier}</div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{role}</div>
      </div>
      <span
        className={`text-[10px] font-mono font-bold px-2 py-0.5 border ${highlight ? "evos-pulse" : ""}`}
        style={{ color, borderColor: color + "55", background: color + "12" }}
      >
        ● {status}
      </span>
    </div>
  );
}
